import { computed, Ref, inject, provide } from "vue";

import { Market, Option, BetData, SportEvent } from "@fresh8/types/src/betData";
import { orderedCompetitorData } from "./lib/orderedCompetitorData";
import { sortOptionsByOdds } from "./lib/sortOptions";
import * as generateLinks from "./lib/generateLinks";

const HANDICAP_BETTING = "Handicap Betting";
const MATCH_WINNER = "Match Winner";
const OVER_UNDER_TOTALS = "Over/Under Totals";

export function createBetData(betData: Ref<BetData>) {
  const markets = computed(() => betData?.value?.markets);
  const marketNames = computed(
    () =>
      (markets.value &&
        Object.values(markets.value as Record<string, Market>).map(
          market => market.marketType
        )) ||
      []
  );

  const clickUrl = computed(() => betData?.value?.clickUrl);
  const targetUrl = computed(() => betData?.value?.targetUrl);
  const prerollTargetUrl = computed(() => betData?.value?.prerollTargetUrl);
  const affiliateId = computed(() => betData?.value?.affiliateId);
  const pixels = computed(() => betData?.value?.pixels);

  const events = computed(() => {
    return (
      betData?.value?.events &&
      generateLinks.events(betData.value.events, {
        clickUrl: clickUrl.value,
        targetUrl: targetUrl.value,
        affiliateId: affiliateId.value
      })
    );
  });

  const options = computed(() => {
    return (
      betData?.value?.options &&
      generateLinks.options(betData.value.options, {
        clickUrl: clickUrl.value,
        targetUrl: targetUrl.value,
        affiliateId: affiliateId.value
      })
    );
  });

  const links = computed(() => {
    const links = betData?.value?.links;

    return (
      links &&
      generateLinks.links(links, {
        clickUrl: clickUrl.value,
        targetUrl: targetUrl.value,
        affiliateId: affiliateId.value
      })
    );
  });

  const offer = computed(() => {
    const offer = betData?.value?.offer;

    return (
      offer &&
      generateLinks.offer(offer, {
        affiliateId: affiliateId.value,
        clickUrl: clickUrl.value
      })
    );
  });

  const operator = computed(() => betData?.value?.operator);
  const operators = computed(() => betData?.value?.operators);

  const catalogId = computed(() => {
    return betData?.value?.analytics.catalogueId;
  });

  const viewId = computed(() => {
    return betData?.value?.analytics.viewId;
  });

  const campaignId = computed(() => {
    return betData?.value?.analytics.campaignId || "";
  });

  const brandId = computed(() => {
    return betData?.value?.analytics.brandId || "";
  });

  const showOffer = computed(() => {
    return (
      (betData?.value?.offer?.body.length || 0) > 0 ||
      (betData?.value?.offer?.header.length || 0) > 0 ||
      (betData?.value?.offer?.buttonText.length || 0) > 0 ||
      (betData?.value?.offer?.terms.length || 0) > 0
    );
  });

  return {
    events,
    operator,
    operators,
    eventIds: computed(() => (events.value && Object.keys(events.value)) || []),
    markets,
    options,
    affiliateId,
    marketNames,
    links,
    pixels,
    catalogId,
    viewId,
    campaignId,
    brandId,
    offer,
    showOffer,
    betData,
    clickUrl,
    targetUrl,
    prerollTargetUrl
  };
}

export type BetDataContext = ReturnType<typeof createBetData>;

export const BetDataSymbol = Symbol("BetDataCompSymbol");

export function provideBetData(adtagData: Ref<BetData>) {
  provide(BetDataSymbol, createBetData(adtagData));
}

export function injectBetData(): BetDataContext | undefined {
  return inject<BetDataContext>(BetDataSymbol);
}

export type BetDataGetterContext = {
  betData: Ref<BetData>;
  loading: Ref<boolean>;
  get?: (
    operator?: string
  ) => { result: Ref<BetData>; loading: Ref<boolean>; error: Ref<boolean> };
};

export const BetDataRefSymbol = Symbol("BetDataRefCompSymbol");
export function provideBetDataRef(betDataContext: BetDataGetterContext): void {
  provide(BetDataRefSymbol, betDataContext);
}
export function injectBetDataRef(): BetDataGetterContext | undefined {
  return inject<BetDataGetterContext>(BetDataRefSymbol);
}

export function getHomeAndAwayOptionsForMarket(
  market: Market | undefined,
  options: Record<string, Option> | undefined
): { home: string; away: string; draw: string } {
  if (!options || !market) {
    return { home: "", draw: "", away: "" };
  }

  return market.options.reduce(
    (acc, curr) => {
      const option = options[curr];
      return {
        ...acc,
        [option.label]: curr
      };
    },
    { home: "", draw: "", away: "" }
  );
}

export function composeMarket(marketId: string) {
  const betData = injectBetData();

  return computed(() => betData?.markets.value?.[marketId]);
}

export function composeEventMarkets(eventId: string) {
  const betData = injectBetData();

  return computed(() => betData?.events.value[eventId]?.markets || []);
}

export function composeEvent(eventId: string) {
  const betData = injectBetData();

  return computed(() => betData?.events.value[eventId]);
}

export function composeBetOption(optionId: string) {
  const betData = injectBetData();

  return computed(() => betData?.options.value[optionId]);
}

export function composeMultiFtrMarkets(eventId: string) {
  const betData = injectBetData();
  const eventMarkets = composeEventMarkets(eventId);

  return computed(() => {
    const matchWinner = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value[marketEvent].marketType === MATCH_WINNER
    );

    const handicapBetting = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value[marketEvent].marketType === HANDICAP_BETTING
    );

    const overUnderTotals = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value[marketEvent].marketType === OVER_UNDER_TOTALS
    );

    return [matchWinner, handicapBetting, overUnderTotals];
  });
}

const name = ["Moneyline", "Spread", "Totals"];

export function composeHomeAwayOptions(marketIds: Ref<(string | undefined)[]>) {
  const betData = injectBetData();

  return computed(() =>
    marketIds.value.map((id, index) => {
      if (id) {
        const market = betData?.markets.value[id];

        if (!market) {
          return {
            options: [],
            displayName: name[index]
          };
        }

        const homeAndAwayOptions = getHomeAndAwayOptionsForMarket(
          market,
          betData?.options.value
        );

        const events = betData?.events.value || "";
        const sport = events[market.eventId]?.sport || "";

        const orderedOptions = orderedCompetitorData<string>(
          homeAndAwayOptions,
          sport
        );

        return {
          displayName: name[index],
          marketId: id,
          options: orderedOptions
        };
      } else {
        return {
          options: [],
          displayName: name[index]
        };
      }
    })
  );
}

export function composeParlayMarketOptions(
  marketIds: Ref<(string | undefined)[]>,
  teamColorsAvailable: Ref<boolean>
) {
  const betData = injectBetData();

  return computed(() =>
    marketIds.value.map(id => {
      if (id) {
        const market = betData?.markets.value[id];

        if (!market) {
          return {
            options: [],
            eventId: ""
          };
        }

        const homeAndAwayOptions = getHomeAndAwayOptionsForMarket(
          market,
          betData?.options.value
        );

        const events = betData?.events.value || "";
        const sport = events[market.eventId]?.sport || "";

        const orderedOptions = orderedCompetitorData<string | undefined>(
          homeAndAwayOptions,
          sport
        );

        return {
          marketId: id,
          eventId: market.eventId,
          options: orderedOptions,
          teamColorsAvailable: teamColorsAvailable.value
        };
      } else {
        return {
          options: []
        };
      }
    })
  );
}

export function composeEventFixture(eventId: string) {
  const event = composeEvent(eventId);
  return computed(() => event.value?.fixture);
}

export function composeEventCompetitors(eventId: string) {
  const event = composeEvent(eventId);
  return computed(
    () =>
      event.value &&
      orderedCompetitorData(event.value.competitors, event.value.sport)
  );
}

export function composeListMarketOptions(marketId: string) {
  const betData = injectBetData();

  return computed(() =>
    betData?.markets.value[marketId]?.options
      .map(optionId => betData.options.value[optionId])
      .sort(sortOptionsByOdds)
  );
}

export function composeEventsByDate() {
  const betData = injectBetData();

  return computed(() => {
    if (!betData) {
      return [];
    }

    const events = betData.events;

    return (
      (betData.events.value &&
        Object.values(events.value as Record<string, SportEvent>).sort(
          (a, b) => {
            if (new Date(a.fixture) < new Date(b.fixture)) {
              return -1;
            }
            if (new Date(a.fixture) > new Date(b.fixture)) {
              return 1;
            }
            return 0;
          }
        )) ||
      []
    );
  });
}

export function insightIsEnabled(marketId: string, enabledInsights: string[]) {
  const availableLeagues: Record<string, string[]> = {
    "American Football": ["NFL"],
    Baseball: ["MLB", "Major League Baseball"],
    Basketball: ["NBA"],
    "Ice Hockey": ["NHL"]
  };

  const betData = injectBetData();

  if (betData) {
    const market = betData.markets.value[marketId];

    if (market) {
      const event = betData.events.value[market.eventId];
      const isQualifyingCompetition = availableLeagues[event.sport].includes(
        event.normalisedCompetition
      );

      if (isQualifyingCompetition) {
        return enabledInsights.includes(event.normalisedCompetition);
      }
    }
  }
  return false;
}
